import React, { useEffect, useRef, useState } from 'react';
import {
  CreditCard,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Download,
  Loader2,
  Banknote,
  FileText,
  Check,
  RefreshCw,
  Upload
} from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import {
  getPaymentStatus,
  getReceipts,
  downloadReceiptPdf,
  confirmPayment,
  uploadMandatoSepa,
  leggiCartaPendente,
  rimuoviCartaPendente,
  isConfermaCartaDefinitiva,
  isCartaPendenteRegistrata,
  CartaPendente,
  PaymentStatusData,
  valutaGateQuota
} from '../services/associatePaymentService';
import { AssociateData } from '../services/otpAuthService';
import AssociateFeePayment from './AssociateFeePayment';
import AssociateIntegrativaRequest from './AssociateIntegrativaRequest';

interface AssociateFeeCardProps {
  associate: AssociateData;
  onAssociateRefresh?: () => void;
}

const AssociateFeeCard: React.FC<AssociateFeeCardProps> = ({ associate, onAssociateRefresh }) => {
  const toast = useToast();
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatusData | null>(null);
  const [receipts, setReceipts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [integrativaPaymentId, setIntegrativaPaymentId] = useState<string | null>(null);
  const [integrativaVoci, setIntegrativaVoci] = useState<any[] | null>(null);
  const [integrativaTotale, setIntegrativaTotale] = useState<number>(0);
  const [showIntegrativa, setShowIntegrativa] = useState(false);
  const [downloadingReceipt, setDownloadingReceipt] = useState<string | null>(null);
  // Carta addebitata su Stripe ma registrazione non ancora confermata (promemoria nel browser)
  const [cartaPendente, setCartaPendente] = useState<CartaPendente | null>(() => leggiCartaPendente(associate.id));
  const [retryingCarta, setRetryingCarta] = useState(false);
  const [uploadingMandato, setUploadingMandato] = useState(false);
  const mandatoInputRef = useRef<HTMLInputElement>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const [statusRes, receiptsRes] = await Promise.all([
        getPaymentStatus(),
        getReceipts()
      ]);
      setPaymentStatus(statusRes.data);
      setReceipts(receiptsRes.data || []);
      // Il promemoria si rilegge a ogni caricamento (può nascere dal form di pagamento)
      // e si pulisce se lo stato mostra già il pagamento registrato.
      const voce = leggiCartaPendente(associate.id);
      if (voce && isCartaPendenteRegistrata(voce, statusRes.data)) {
        rimuoviCartaPendente(associate.id, voce.paymentIntentId);
        setCartaPendente(null);
      } else {
        setCartaPendente(voce);
      }
    } catch (error: any) {
      console.error('Errore caricamento stato pagamento:', error);
      // Senza stato certo dal server non si mostrano azioni di pagamento
      setPaymentStatus(null);
      setLoadError(true);
      setCartaPendente(leggiCartaPendente(associate.id));
      // Non mostrare errore se 404 (nessun settings configurato)
      if (error.response?.status !== 404) {
        toast.error('Errore caricamento dati pagamento');
      }
    } finally {
      setLoading(false);
    }
  };

  // Ritenta la registrazione di una carta già addebitata (conferma idempotente lato server).
  const riprovaCartaPendente = async (voce: CartaPendente, manuale: boolean) => {
    setRetryingCarta(true);
    try {
      const res = await confirmPayment(voce.paymentIntentId);
      rimuoviCartaPendente(associate.id, voce.paymentIntentId);
      setCartaPendente(null);
      if (res?.data?.duplicate === true || res?.data?.payment?.isDuplicate === true) {
        toast.warning('Pagamento registrato come possibile doppio addebito: la segreteria verificherà.', { duration: 10000 });
      } else {
        toast.success('Pagamento con carta registrato');
      }
      loadData();
      onAssociateRefresh?.();
    } catch (error: any) {
      if (isConfermaCartaDefinitiva(error)) {
        // Rimborsato o inesistente: non c'è nulla da registrare
        rimuoviCartaPendente(associate.id, voce.paymentIntentId);
        setCartaPendente(null);
        if (manuale) toast.info(error.response?.data?.message || 'Nessun pagamento da registrare');
        loadData();
      } else if (manuale) {
        toast.error('Registrazione non ancora completata', 'Riprova tra qualche minuto o contatta la segreteria.');
      }
    } finally {
      setRetryingCarta(false);
    }
  };

  useEffect(() => {
    loadData();
    const voce = leggiCartaPendente(associate.id);
    if (voce) riprovaCartaPendente(voce, false);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, []);

  // Mandato SEPA firmato di un RID in corso (anche dopo un upload fallito alla richiesta)
  const handleMandatoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!['application/pdf', 'image/jpeg', 'image/png'].includes(file.type)) {
      toast.error('Solo file PDF, JPG o PNG sono accettati');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Il file non può superare 10MB');
      return;
    }
    try {
      setUploadingMandato(true);
      const formData = new FormData();
      formData.append('mandato', file);
      await uploadMandatoSepa(formData);
      toast.success('Mandato SEPA caricato');
      loadData();
    } catch (error: any) {
      console.error('Errore upload mandato SEPA:', error);
      toast.error(error.response?.data?.message || 'Errore durante il caricamento del mandato SEPA');
    } finally {
      setUploadingMandato(false);
    }
  };

  const handleDownloadReceipt = async (receiptId: string) => {
    try {
      setDownloadingReceipt(receiptId);
      await downloadReceiptPdf(receiptId);
      toast.success('Download ricevuta avviato');
    } catch (error) {
      console.error('Errore download ricevuta:', error);
      toast.error('Errore durante il download della ricevuta');
    } finally {
      setDownloadingReceipt(null);
    }
  };

  const handlePaymentComplete = () => {
    setShowPayment(false);
    loadData();
    onAssociateRefresh?.();
  };

  // Determina badge e stato
  const getStatusBadge = () => {
    const stato = associate.statoTesseramento;

    if (stato === 'PAGATO') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
          <CheckCircle className="w-3.5 h-3.5" />
          PAGATO
        </span>
      );
    }

    if (stato === 'ATTIVATO') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
          <Clock className="w-3.5 h-3.5" />
          ATTIVATO - Rate in corso
        </span>
      );
    }

    if (stato === 'IN ATTIVAZIONE') {
      // Check if there's a pending bank transfer
      if (paymentStatus?.hasPendingPayment && paymentStatus.stato === 'in_attesa' && paymentStatus.metodoPagamento === 'bonifico') {
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
            <Clock className="w-3.5 h-3.5" />
            IN ATTESA CONFERMA
          </span>
        );
      }
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
          <AlertCircle className="w-3.5 h-3.5" />
          IN ATTIVAZIONE
        </span>
      );
    }

    if (stato === 'NON PAGATO') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
          <XCircle className="w-3.5 h-3.5" />
          NON PAGATO
        </span>
      );
    }

    // FUTURO ASSOCIATO o vuoto
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
        <Clock className="w-3.5 h-3.5" />
        In attesa di verifica
      </span>
    );
  };

  // Mostra il metodo di pagamento in modo leggibile
  const formatPaymentMethod = (method?: string) => {
    switch (method) {
      case 'carta': return 'Carta di credito';
      case 'bonifico': return 'Bonifico bancario';
      case 'rid': return 'RID bancario';
      default: return method || '-';
    }
  };

  // Verifica se puo pagare la quota nazionale: decide il server (puoPagareQuota);
  // con un backend che non espone il campo vale il gate storico
  // (IN ISCRIZIONE + IN ATTIVAZIONE, nessun pagamento principale in corso).
  // Solo se lo stato pagamenti e' stato caricato correttamente.
  const gate = valutaGateQuota(loadError ? null : paymentStatus, associate);
  // Con una carta addebitata in attesa di registrazione non si propone alcun pagamento
  const cartaInRegistrazione = !!cartaPendente;
  const canPay = gate.puoPagare && !cartaInRegistrazione;
  // Rinnovo: l'associato e' in regola e la campagna rinnovi dell'anno e' aperta
  const canRenew = canPay && gate.tipo === 'rinnovo';
  const inRegola = associate.statoTesseramento === 'PAGATO' || associate.statoTesseramento === 'ATTIVATO';

  // Integrativa pagata con bonifico in attesa di verifica: nessun bottone Paga
  const isIntegrativaBonificoInVerifica = (pi: any) =>
    !!pi?.bonificoCaricato || pi?.metodoPagamento === 'bonifico';

  // Ha un bonifico in attesa di conferma
  const hasPendingBankTransfer = paymentStatus?.hasPendingPayment &&
    paymentStatus.stato === 'in_attesa' &&
    paymentStatus.metodoPagamento === 'bonifico';

  // RID con rate ancora aperte (iscrizione o rinnovo): le rate si mostrano sempre
  const ridInCorso = !!paymentStatus?.hasPendingPayment &&
    paymentStatus.metodoPagamento === 'rid' &&
    paymentStatus.stato === 'parziale';

  // Payment modal open
  if (showPayment) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-800 p-6 transition-shadow duration-200 hover:shadow-xl anim-fade-up" style={{ animationDelay: '120ms' }}>
        <AssociateFeePayment
          associate={associate}
          onPaymentComplete={() => { setIntegrativaPaymentId(null); setIntegrativaVoci(null); setIntegrativaTotale(0); handlePaymentComplete(); }}
          onClose={() => { setIntegrativaPaymentId(null); setIntegrativaVoci(null); setIntegrativaTotale(0); setShowPayment(false); loadData(); }}
          disableRid={!!integrativaPaymentId}
          paymentId={integrativaPaymentId}
          overrideVociQuota={integrativaVoci || undefined}
          overrideTotale={integrativaTotale || undefined}
        />
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-800 p-6 transition-shadow duration-200 hover:shadow-xl anim-fade-up" style={{ animationDelay: '120ms' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center shadow-md shadow-purple-500/20">
            <CreditCard className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            Tesseramento
          </h2>
        </div>
        {!loading && getStatusBadge()}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Caricamento...</span>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Stato pagamenti non caricato: nessuna azione di pagamento */}
          {loadError && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center justify-between gap-3">
              <p className="text-sm text-red-800 dark:text-red-200">
                Impossibile caricare lo stato dei pagamenti.
              </p>
              <button
                onClick={loadData}
                className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/40 hover:bg-red-200 dark:hover:bg-red-900/60 rounded-md transition-colors"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Riprova
              </button>
            </div>
          )}

          {/* Carta addebitata, registrazione sul portale non ancora confermata */}
          {cartaPendente && (
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-center justify-between gap-3">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                <strong>Pagamento con carta ricevuto: registrazione in corso…</strong> Non ripetere il pagamento.
              </p>
              <button
                onClick={() => riprovaCartaPendente(cartaPendente, true)}
                disabled={retryingCarta}
                className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-amber-800 dark:text-amber-200 bg-amber-100 dark:bg-amber-900/40 hover:bg-amber-200 dark:hover:bg-amber-900/60 rounded-md transition-colors disabled:opacity-50"
              >
                {retryingCarta ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Riprova ora
              </button>
            </div>
          )}

          {/* === RINNOVO (campagna rinnovi aperta, associato in regola) === */}
          {canRenew && (
            <div className="p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg space-y-2">
              <p className="text-sm text-purple-900 dark:text-purple-100">
                <strong>È aperto il rinnovo {gate.anno}.</strong> Rinnova la quota per restare in regola anche per il {gate.anno}.
              </p>
              <button
                onClick={() => { setIntegrativaPaymentId(null); setIntegrativaVoci(null); setIntegrativaTotale(0); setShowPayment(true); }}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-semibold rounded-lg shadow-md shadow-purple-500/20 transition-all"
              >
                <RefreshCw className="w-5 h-5" />
                Rinnova la quota {gate.anno}
              </button>
            </div>
          )}
          {!canRenew && inRegola && hasPendingBankTransfer && (
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                <strong>Bonifico del rinnovo {gate.anno} registrato.</strong> In attesa di conferma dalla segreteria.
              </p>
            </div>
          )}
          {gate.rinnovato && (
            <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-2">
              <CheckCircle className="w-4 h-4 flex-shrink-0 text-green-600 dark:text-green-400" />
              <p className="text-sm font-semibold text-green-800 dark:text-green-200">Quota {gate.anno} pagata</p>
            </div>
          )}

          {/* === PAGATO === */}
          {associate.statoTesseramento === 'PAGATO' && (
            <>
              {/* Ricevute */}
              {receipts.length > 0 && (
                <div className="space-y-2">
                  {receipts.map((receipt) => (
                    <div key={receipt._id} className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-green-600 dark:text-green-400" />
                        <div>
                          <p className="text-sm font-semibold text-green-800 dark:text-green-200">
                            Ricevuta {receipt.numeroRicevuta}
                          </p>
                          <p className="text-xs text-green-600 dark:text-green-400">
                            {new Date(receipt.dataEmissione).toLocaleDateString('it-IT')} - {receipt.importoTotale?.toFixed(2)} EUR
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDownloadReceipt(receipt._id)}
                        disabled={downloadingReceipt === receipt._id}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/40 hover:bg-green-200 dark:hover:bg-green-900/60 rounded-md transition-colors"
                      >
                        {downloadingReceipt === receipt._id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Download className="w-3.5 h-3.5" />
                        )}
                        PDF
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Quote integrative da pagare */}
              {paymentStatus?.pendingIntegrative && paymentStatus.pendingIntegrative.length > 0 && (
                <div className="space-y-2">
                  {paymentStatus.pendingIntegrative.map((pi: any) => (
                    <div key={pi._id} className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                            {isIntegrativaBonificoInVerifica(pi) ? 'Quota integrativa' : 'Quota integrativa da pagare'}
                          </p>
                          {pi.vociQuota?.map((v: any, i: number) => (
                            <p key={i} className="text-xs text-amber-600 dark:text-amber-400">
                              {v.descrizione} — €{v.importo?.toFixed(2)}
                            </p>
                          ))}
                        </div>
                        {isIntegrativaBonificoInVerifica(pi) ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-300 text-right">
                            <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                            Bonifico in verifica dalla segreteria
                          </span>
                        ) : cartaInRegistrazione ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-300 text-right">
                            <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                            Registrazione in corso
                          </span>
                        ) : (
                          <button
                            onClick={() => { setIntegrativaPaymentId(pi._id); setIntegrativaVoci(pi.vociQuota); setIntegrativaTotale(pi.importoTotale); setShowPayment(true); }}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white bg-amber-500 hover:bg-amber-600 rounded-md transition-colors"
                          >
                            <CreditCard className="w-3.5 h-3.5" />
                            Paga €{pi.importoTotale?.toFixed(2)}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Dettagli pagamenti (espandibile) — mostra TUTTI i pagamenti dell'anno */}
              {paymentStatus?.payments && paymentStatus.payments.length > 0 && (
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                >
                  {showDetails ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  {showDetails ? 'Nascondi dettagli' : `Mostra dettagli pagament${paymentStatus.payments.length > 1 ? 'i' : 'o'}`}
                </button>
              )}
              {showDetails && paymentStatus?.payments && (
                <div className="space-y-3">
                  {paymentStatus.payments.filter((p: any) => p.stato !== 'annullato').map((p: any, idx: number) => (
                    <div key={p._id || idx} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg space-y-2 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-semibold uppercase text-gray-400 dark:text-gray-500">
                          {p.vociQuota?.some((v: any) => v.tipo === 'nazionale') ? 'Quota nazionale' : 'Quota integrativa'}
                        </span>
                        {p.isDuplicate ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-2xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                            <AlertCircle className="w-3 h-3" />
                            Possibile doppio pagamento — in verifica
                          </span>
                        ) : (
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-2xs font-medium ${
                            p.stato === 'confermato' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                            p.stato === 'in_attesa' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
                            'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                          }`}>{p.stato}</span>
                        )}
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Metodo</span>
                        <span className="font-medium text-gray-900 dark:text-white">{formatPaymentMethod(p.metodoPagamento)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Importo</span>
                        <span className="font-medium text-gray-900 dark:text-white">{p.importoTotale?.toFixed(2)} EUR</span>
                      </div>
                      {p.dataPagamento && (
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">Data</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {new Date(p.dataPagamento).toLocaleDateString('it-IT')}
                          </span>
                        </div>
                      )}
                      {p.vociQuota && p.vociQuota.length > 0 && (
                        <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                          {p.vociQuota.map((voce: any, i: number) => (
                            <div key={i} className="flex justify-between text-xs">
                              <span className="text-gray-600 dark:text-gray-400">{voce.descrizione}</span>
                              <span className="text-gray-900 dark:text-white">{voce.importo?.toFixed(2)} EUR</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* === RID in corso: ATTIVATO, oppure rate aperte con qualsiasi stato tesseramento (rinnovo) === */}
          {paymentStatus?.rate && (ridInCorso || associate.statoTesseramento === 'ATTIVATO') && (
            <>
              {ridInCorso && associate.statoTesseramento !== 'ATTIVATO' && (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    <strong>RID {gate.anno} in corso.</strong> Le rate vengono addebitate alle scadenze indicate.
                  </p>
                </div>
              )}
              <div className="space-y-2">
                {paymentStatus.rate.map((rata) => (
                  <div key={rata.numero} className="flex items-center justify-between p-2.5 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2">
                      {rata.stato === 'confermato' ? (
                        <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                      ) : rata.stato === 'scaduto' ? (
                        <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                      ) : (
                        <Clock className="w-4 h-4 text-amber-500 dark:text-amber-400" />
                      )}
                      <span className="text-sm text-gray-900 dark:text-white">
                        Rata {rata.numero}
                      </span>
                      {rata.stato === 'scaduto' && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-2xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                          Scaduta
                        </span>
                      )}
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">
                        {rata.importo?.toFixed(2)} EUR
                      </span>
                      <p className={`text-xs ${rata.stato === 'scaduto' ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
                        {rata.stato === 'confermato'
                          ? `Pagata ${(rata.confermatoIl || rata.dataPagamento) ? new Date((rata.confermatoIl || rata.dataPagamento) as string).toLocaleDateString('it-IT') : ''}`
                          : rata.stato === 'scaduto'
                            ? `Scaduta il ${new Date(rata.scadenza).toLocaleDateString('it-IT')}`
                            : `Scadenza ${new Date(rata.scadenza).toLocaleDateString('it-IT')}`
                        }
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Mandato SEPA firmato: caricabile (o sostituibile) finché il RID è in corso */}
              {ridInCorso && (
                <div className="flex items-center justify-between gap-3 p-2.5 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div>
                    <p className="text-sm text-gray-900 dark:text-white">Mandato SEPA firmato</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {paymentStatus.mandatoSepa?.uploadedAt
                        ? `Caricato il ${new Date(paymentStatus.mandatoSepa.uploadedAt).toLocaleDateString('it-IT')}`
                        : 'Non ancora caricato (PDF, JPG o PNG, max 10MB)'}
                    </p>
                  </div>
                  <input
                    ref={mandatoInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                    onChange={handleMandatoUpload}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => mandatoInputRef.current?.click()}
                    disabled={uploadingMandato}
                    className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 border border-purple-200 dark:border-purple-800 rounded-md transition-colors disabled:opacity-50"
                  >
                    {uploadingMandato ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                    {paymentStatus.mandatoSepa?.uploadedAt ? 'Sostituisci' : 'Carica mandato'}
                  </button>
                </div>
              )}

              {/* Ricevute parziali */}
              {associate.statoTesseramento === 'ATTIVATO' && receipts.length > 0 && (
                <div className="pt-2">
                  {receipts.map((receipt) => (
                    <button
                      key={receipt._id}
                      onClick={() => handleDownloadReceipt(receipt._id)}
                      disabled={downloadingReceipt === receipt._id}
                      className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      {downloadingReceipt === receipt._id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Download className="w-3 h-3" />
                      )}
                      Ricevuta {receipt.numeroRicevuta}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {/* === IN ATTIVAZIONE (puo pagare) === */}
          {associate.statoTesseramento === 'IN ATTIVAZIONE' && (
            <>
              {hasPendingBankTransfer ? (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    <strong>Bonifico registrato.</strong> In attesa di conferma dalla segreteria.
                  </p>
                  {paymentStatus?.bonifico?.uploadedAt && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                      Inviato il {new Date(paymentStatus.bonifico.uploadedAt).toLocaleDateString('it-IT')}
                    </p>
                  )}
                </div>
              ) : canPay ? (
                <button
                  onClick={() => { setIntegrativaPaymentId(null); setIntegrativaVoci(null); setIntegrativaTotale(0); setShowPayment(true); }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-semibold rounded-lg shadow-md shadow-purple-500/20 transition-all"
                >
                  <Banknote className="w-5 h-5" />
                  Paga quota associativa
                </button>
              ) : (gate.motivo && !paymentStatus?.hasPendingPayment) ? (
                // Chi deve iscriversi ma non può ancora pagare: il motivo dal server gli è utile
                <div className="p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-400">{gate.motivo}</p>
                </div>
              ) : null}

              {/* Quote integrative pendenti (anche prima del pagamento nazionale) */}
              {paymentStatus?.pendingIntegrative && paymentStatus.pendingIntegrative.length > 0 && (
                <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-xs text-blue-700 dark:text-blue-300 font-medium mb-1">Quote integrative in attesa:</p>
                  {paymentStatus.pendingIntegrative.map((pi: any) => (
                    <p key={pi._id} className="text-xs text-blue-600 dark:text-blue-400">
                      {pi.vociQuota?.map((v: any) => v.descrizione).join(', ')} — €{pi.importoTotale?.toFixed(2)}
                    </p>
                  ))}
                  <p className="text-xs text-blue-500 dark:text-blue-400 mt-1 italic">
                    Potrai pagare le quote integrative dopo il pagamento della quota nazionale.
                  </p>
                </div>
              )}
            </>
          )}

          {/* === NON PAGATO === */}
          {associate.statoTesseramento === 'NON PAGATO' && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-800 dark:text-red-200">
                La quota associativa per l'anno in corso non risulta pagata.
              </p>
            </div>
          )}

          {/* === FUTURO ASSOCIATO / vuoto === */}
          {(!associate.statoTesseramento || associate.statoTesseramento === 'FUTURO ASSOCIATO') && (
            <div className="p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                La segreteria sta verificando la tua domanda di iscrizione.
              </p>
            </div>
          )}

          {/* Info aggiuntive (tipo pagamento, integrativa) - sempre visibili */}
          <div className="space-y-3 pt-2">
            {associate.tipoPagamento && associate.tipoPagamento !== 'NON SPECIFICATO' && (
              <div className="pb-3 border-b border-gray-100 dark:border-gray-800">
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1 block">Tipo Pagamento</label>
                <p className="text-sm font-semibold text-gray-900 dark:text-white capitalize">
                  {associate.tipoPagamento}
                </p>
              </div>
            )}

            {associate.integrativa && (
              <div className="pb-3 border-b border-gray-100 dark:border-gray-800">
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1 block">Categoria Integrativa</label>
                {(() => {
                  const settori = (associate.integrativa || '').split(' ').filter(Boolean);
                  if (settori.length === 0) {
                    return <p className="text-sm font-semibold text-gray-900 dark:text-white">—</p>;
                  }
                  return (
                    <div className="flex flex-wrap gap-1.5">
                      {settori.map((settore) => (
                        <span
                          key={settore}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-2xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300"
                        >
                          {settore}
                        </span>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}

            {associate.totIntegrativaSede && (
              <div className="pb-3 border-b border-gray-100 dark:border-gray-800">
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1 block">Numero Sedi Integrative</label>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {associate.totIntegrativaSede}
                </p>
              </div>
            )}
          </div>

          {/* Richiesta integrativa */}
          {associate.statoTesseramento === 'PAGATO' && (
            <div className="pt-1">
              {showIntegrativa ? (
                <AssociateIntegrativaRequest
                  associate={associate}
                  pendingSettori={(paymentStatus?.pendingIntegrative || [])
                    .flatMap((pi: any) => (pi.vociQuota || []).map((v: any) => (v.tipo || '').replace('integrativa_', '').toUpperCase()))
                    .filter(Boolean)}
                  onClose={() => setShowIntegrativa(false)}
                />
              ) : (
                <button
                  onClick={() => setShowIntegrativa(true)}
                  className="text-xs text-purple-600 dark:text-purple-400 hover:underline"
                >
                  Richiedi quota integrativa
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AssociateFeeCard;
